import * as pc from 'playcanvas';
import {
  PlayCanvasMeasureGizmo,
  type GizmoHighlight,
  type GizmoSyncState,
} from '@viewer/playcanvas/PlayCanvasMeasureGizmo';
import type { Vec3 } from '@viewer/overlay/measureTypes';

export type { GizmoHighlight };

const DOT_RADIUS = 0.035;
const LINE_THICKNESS = 0.012;

function makeMeasureMaterial(r: number, g: number, b: number, opacity = 1): pc.StandardMaterial {
  const mat = new pc.StandardMaterial();
  mat.diffuse.set(0, 0, 0);
  mat.emissive.set(r, g, b);
  mat.emissiveIntensity = 1;
  mat.opacity = opacity;
  mat.blendType = opacity >= 0.999 ? pc.BLEND_NONE : pc.BLEND_NORMAL;
  mat.depthWrite = opacity >= 0.999;
  mat.depthTest = true;
  mat.useLighting = false;
  mat.update();
  return mat;
}

interface SegmentEntities {
  dotA: pc.Entity;
  dotB: pc.Entity;
  line: pc.Entity;
}

/**
 * Opaque world-space measure markers — depth-tested against splats like panels.
 */
export class PlayCanvasMeasureWorld {
  private readonly root: pc.Entity;
  private readonly dotMat: pc.StandardMaterial;
  private readonly previewDotMat: pc.StandardMaterial;
  private readonly lineMat: pc.StandardMaterial;
  private readonly previewLineMat: pc.StandardMaterial;
  private readonly segments = new Map<string, SegmentEntities>();
  private readonly draftDotA: pc.Entity;
  private readonly draftDotB: pc.Entity;
  private readonly draftLine: pc.Entity;
  private readonly gizmo: PlayCanvasMeasureGizmo;

  private readonly tmpMid = new pc.Vec3();
  private readonly tmpDir = new pc.Vec3();
  private readonly tmpCamPos = new pc.Vec3();

  constructor(app: pc.AppBase) {
    this.root = new pc.Entity('measure-world');
    this.root.enabled = false;
    app.root.addChild(this.root);

    this.dotMat = makeMeasureMaterial(1, 1, 1);
    this.previewDotMat = makeMeasureMaterial(1, 1, 1, 0.55);
    this.lineMat = makeMeasureMaterial(0.92, 0.92, 0.92);
    this.previewLineMat = makeMeasureMaterial(0.92, 0.92, 0.92, 0.55);

    this.draftDotA = this.createDot('measure-draft-a', this.previewDotMat);
    this.draftDotB = this.createDot('measure-draft-b', this.previewDotMat);
    this.draftLine = this.createLine('measure-draft-line', this.previewLineMat);
    this.draftDotA.enabled = false;
    this.draftDotB.enabled = false;
    this.draftLine.enabled = false;
    this.root.addChild(this.draftDotA);
    this.root.addChild(this.draftDotB);
    this.root.addChild(this.draftLine);
    this.gizmo = new PlayCanvasMeasureGizmo(this.root);
  }

  dispose(): void {
    for (const id of [...this.segments.keys()]) this.removeSegment(id);
    this.gizmo.dispose();
    this.draftDotA.destroy();
    this.draftDotB.destroy();
    this.draftLine.destroy();
    this.dotMat.destroy();
    this.previewDotMat.destroy();
    this.lineMat.destroy();
    this.previewLineMat.destroy();
    this.root.destroy();
  }

  setVisible(visible: boolean): void {
    this.root.enabled = visible;
    if (visible) return;
    for (const seg of this.segments.values()) {
      seg.dotA.enabled = false;
      seg.dotB.enabled = false;
      seg.line.enabled = false;
    }
    this.draftDotA.enabled = false;
    this.draftDotB.enabled = false;
    this.draftLine.enabled = false;
  }

  syncEditGizmo(state: GizmoSyncState): void {
    this.gizmo.sync(state);
  }

  gizmoAxisLength(point: Vec3, cameraEntity: pc.Entity): number {
    this.tmpCamPos.copy(cameraEntity.getPosition());
    return PlayCanvasMeasureGizmo.axisLengthAtPoint(point, this.tmpCamPos);
  }

  syncDraft(a: Vec3 | null, b: Vec3 | null): void {
    if (!a || !b) {
      this.draftDotA.enabled = false;
      this.draftDotB.enabled = false;
      this.draftLine.enabled = false;
      return;
    }
    this.placeDot(this.draftDotA, a);
    this.placeDot(this.draftDotB, b);
    this.placeLine(this.draftLine, a, b);
  }

  syncMeasurement(id: string, a: Vec3, b: Vec3): void {
    let seg = this.segments.get(id);
    if (!seg) {
      seg = {
        dotA: this.createDot(`${id}-a`, this.dotMat),
        dotB: this.createDot(`${id}-b`, this.dotMat),
        line: this.createLine(`${id}-line`, this.lineMat),
      };
      this.root.addChild(seg.dotA);
      this.root.addChild(seg.dotB);
      this.root.addChild(seg.line);
      this.segments.set(id, seg);
    }
    this.placeDot(seg.dotA, a);
    this.placeDot(seg.dotB, b);
    this.placeLine(seg.line, a, b);
  }

  removeSegment(id: string): void {
    const seg = this.segments.get(id);
    if (!seg) return;
    seg.dotA.destroy();
    seg.dotB.destroy();
    seg.line.destroy();
    this.segments.delete(id);
  }

  private createDot(name: string, material: pc.Material): pc.Entity {
    const entity = new pc.Entity(name);
    entity.addComponent('render', {
      type: 'sphere',
      material,
      castShadows: false,
      receiveShadows: false,
      layers: [pc.LAYERID_WORLD],
    });
    this.tagDepth(entity);
    return entity;
  }

  private createLine(name: string, material: pc.Material): pc.Entity {
    const entity = new pc.Entity(name);
    entity.addComponent('render', {
      type: 'box',
      material,
      castShadows: false,
      receiveShadows: false,
      layers: [pc.LAYERID_WORLD],
    });
    this.tagDepth(entity);
    return entity;
  }

  private tagDepth(entity: pc.Entity): void {
    if (!entity.render) return;
    for (const mi of entity.render.meshInstances) {
      mi.drawOrder = 0;
    }
  }

  private placeDot(entity: pc.Entity, p: Vec3): void {
    entity.enabled = true;
    entity.setLocalPosition(p.x, p.y, p.z);
    entity.setLocalScale(DOT_RADIUS, DOT_RADIUS, DOT_RADIUS);
  }

  private placeLine(entity: pc.Entity, a: Vec3, b: Vec3): void {
    this.tmpDir.set(b.x - a.x, b.y - a.y, b.z - a.z);
    const len = this.tmpDir.length();
    if (len < 1e-4) {
      entity.enabled = false;
      return;
    }
    this.tmpMid.set((a.x + b.x) * 0.5, (a.y + b.y) * 0.5, (a.z + b.z) * 0.5);
    entity.enabled = true;
    entity.setLocalPosition(this.tmpMid.x, this.tmpMid.y, this.tmpMid.z);
    entity.up.set(0, 1, 0);
    entity.lookAt(b.x, b.y, b.z);
    entity.setLocalScale(LINE_THICKNESS, LINE_THICKNESS, len);
  }
}
